
import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop, Watch} from 'vue-property-decorator';
import {IWhatsappMessage} from '@/model/Helpdesk/IWhatsapp';
import WhatsappMessageBubble from '@/components/helpdesk/WhatsappMessageBubble.vue';

interface ThreadItem {
  day?: string;
  message?: IWhatsappMessage;
}

// Distance from the bottom (px) under which new messages keep the view pinned to the end.
const STICKY_BOTTOM = 80;

// Scrollable chat: day separators + bubbles. Stays at the bottom when new messages arrive unless
// the agent scrolled up to read history; loading older messages keeps the current position.
@Component({name: 'WhatsappChatThread', components: {WhatsappMessageBubble}})
export default class WhatsappChatThread extends Vue {
  @Prop({required: true}) messages!: IWhatsappMessage[];
  @Prop({type: Boolean, default: false}) hasOlder!: boolean;
  @Prop({type: Boolean, default: false}) loading!: boolean;
  @Prop({type: Boolean, default: false}) loadingOlder!: boolean;
  // Changes when another conversation opens: always jump to its last message.
  @Prop({default: null}) conversationId!: number | null;

  private atBottom = true;

  get items(): ThreadItem[] {
    const items: ThreadItem[] = [];
    let lastDay = '';
    this.messages.forEach((message) => {
      const day = this.dayLabel(message.created_at);
      if (day !== lastDay) {
        items.push({day});
        lastDay = day;
      }
      items.push({message});
    });
    return items;
  }

  @Watch('conversationId')
  onConversationChange() {
    this.atBottom = true;
  }

  @Watch('messages')
  onMessagesChange(next: IWhatsappMessage[], prev: IWhatsappMessage[]) {
    const scroller = this.$refs.scroller as HTMLElement;
    if (!scroller) return;
    const prependedOlder = prev && prev.length && next.length > prev.length && next[0].id !== prev[0].id
      && next[next.length - 1].id === prev[prev.length - 1].id;
    const previousHeight = scroller.scrollHeight;
    this.$nextTick(() => {
      if (prependedOlder) {
        scroller.scrollTop += scroller.scrollHeight - previousHeight;
      } else if (this.atBottom) {
        scroller.scrollTop = scroller.scrollHeight;
      }
    });
  }

  onScroll() {
    const scroller = this.$refs.scroller as HTMLElement;
    this.atBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < STICKY_BOTTOM;
  }

  private dayLabel(date: string): string {
    const d = new Date(date);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return this.$t('helpdesk.whatsapp.thread.today') as string;
    if (d.toDateString() === yesterday.toDateString()) return this.$t('helpdesk.whatsapp.thread.yesterday') as string;
    return d.toLocaleDateString('es-ES', {weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'});
  }
}
